/**
 * Reading from the API in a component, with honest states (design 10.1: loading never reads as a finding or an
 * all-clear): `data` is null until the answer for *this* path has arrived, so a page that moves from one client to
 * another never shows the first client's rows under the second client's name. A reload keeps the rows on screen
 * until the new answer replaces them.
 */

import { useCallback, useEffect, useState } from 'react'

import { api } from './api'

export interface Loaded<T> {
  data: T | null
  error: unknown
  loading: boolean
  reload: () => void
}

interface State<T> {
  path: string | null
  data: T | null
  error: unknown
}

export function useLoad<T>(path: string | null): Loaded<T> {
  const [state, setState] = useState<State<T>>({ path: null, data: null, error: null })
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!path) return
    let live = true
    api.get<T>(path).then(
      (data) => {
        if (live) setState({ path, data, error: null })
      },
      (error: unknown) => {
        if (live) setState({ path, data: null, error })
      },
    )
    return () => {
      live = false
    }
  }, [path, tick])

  const reload = useCallback(() => setTick((n) => n + 1), [])
  const current = state.path === path
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    loading: path !== null && !current,
    reload,
  }
}

/** One act at a time: busy while it runs, its error kept for the screen, a note when it went through. */
export function useAct() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [note, setNote] = useState<string | null>(null)
  const run = useCallback(async (fn: () => Promise<unknown>, done?: string): Promise<boolean> => {
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      await fn()
      if (done) setNote(done)
      return true
    } catch (err) {
      setError(err)
      return false
    } finally {
      setBusy(false)
    }
  }, [])
  return { run, busy, error, note, setError, setNote }
}
